import React, { useState, useEffect, useMemo } from 'react';
import { Box } from '@mui/material';
import Drawer from '@mui/material/Drawer';
import FilterAltOutlinedIcon from '@mui/icons-material/FilterAltOutlined';
import FilterAltIcon from '@mui/icons-material/FilterAlt';
import useMediaQuery from '@mui/material/useMediaQuery';
import { GridContainer, GridItem, Button } from '../index';
import { Typography, IconButton } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { DrawerConstant } from '../../../constants/DrawerConstants';
import useStyles from './style';
import FILTER_CONSTANTS, { FILTER_VALUES } from '../../../constants/FilterConstants';
import SortContent from './SortContent';
import FilterContent from './FilterContent';
import { parseValidDayjs } from '../../../utils/dayjsDate';

const APPROVED_POSTING_DATE_FIELDS = new Set([FILTER_VALUES.START_DATE, FILTER_VALUES.END_DATE]);

const hasCompleteDateRange = (dateRange = {}) => Boolean(dateRange?.from && dateRange?.to);

const syncSeedliveMissingDatesToggle = (currentFilterData = [], isDisabled = false) =>
  currentFilterData.map((item) => {
    if (item.value !== FILTER_VALUES.SEEDLIVE_MISSING_DATES) return item;
    return {
      ...item,
      disabled: isDisabled,
    };
  });

const syncApprovedPostingDateBounds = (currentFilterData = [], nextFilterParams = {}) =>
  currentFilterData.map((item) => {
    if (item.value === FILTER_VALUES.START_DATE) {
      return {
        ...item,
        maxDate: nextFilterParams[FILTER_VALUES.END_DATE] || undefined,
      };
    }
    if (item.value === FILTER_VALUES.END_DATE) {
      return {
        ...item,
        minDate: nextFilterParams[FILTER_VALUES.START_DATE] || undefined,
      };
    }
    return item;
  });

const validateApprovedPostingDateRange = (previousFilterParams = {}, nextFilterParams = {}, changedField = '') => {
  const parsedStartDate = parseValidDayjs(nextFilterParams[FILTER_VALUES.START_DATE], {
    startOfDay: true,
    fallback: null,
  });
  const parsedEndDate = parseValidDayjs(nextFilterParams[FILTER_VALUES.END_DATE], {
    startOfDay: true,
    fallback: null,
  });

  if (parsedStartDate && parsedEndDate && parsedEndDate.isBefore(parsedStartDate, 'day')) {
    return {
      ...nextFilterParams,
      [changedField]: previousFilterParams?.[changedField] ?? '',
    };
  }

  return nextFilterParams;
};

export default function NewFilterDrawer(props) {
  const { DRAWER_TYPES, DIRECTION } = FILTER_CONSTANTS;
  const matches = useMediaQuery((theme) => theme.breakpoints.up('md'));
  const width = matches ? DrawerConstant.FILTER_DRAWER_MD_UP : DrawerConstant.FILTER_DRAWER_MD_DOWN;
  const classes = useStyles();

  const {
    name = '',
    title = '',
    filteredValues = {},
    onApplyFilter = () => {},
    onReset,
    drawerType = DRAWER_TYPES.FILTER_DRAWER,
    startIcon = <FilterAltOutlinedIcon />,
    onSortSelect,
    selectedSort,
    sortData,
    filterData: initialFilterData,
    getStatesBasedOnCountryId = () => {},
    getCitiesBasedOnStateId = () => {},
    emptyFilterLength,
  } = props;
  const [filter, setFilter] = useState(false);
  const [filterParams, setFilterParams] = useState(filteredValues);
  const [isFilterApplied, setFilterApplied] = useState(false);
  const [filterData, setFilterData] = useState(initialFilterData);

  const startIconParam = isFilterApplied ? <FilterAltIcon /> : startIcon;

  useEffect(() => {
    setFilterData(initialFilterData);
  }, [initialFilterData]);

  useEffect(() => {
    if (!filter || !(FILTER_VALUES.SEEDLIVE_MISSING_DATES in filterParams)) return;
    const isMissingDatesDisabled = !hasCompleteDateRange(filterParams[FILTER_VALUES.DATE_RANGE]);
    setFilterData((prevFilterData) => syncSeedliveMissingDatesToggle(prevFilterData, isMissingDatesDisabled));
  }, [filter, filterParams]);

  const filteredValuesKey = useMemo(() => JSON.stringify(filteredValues), [filteredValues]);

  useEffect(() => {
    if (filteredValuesKey !== JSON.stringify(filterParams)) setFilterParams(filteredValues);
    setFilterApplied(filteredValuesKey?.length > emptyFilterLength);
  }, [filteredValuesKey, emptyFilterLength]);

  const toggleDrawer =
    (open, apply = false, reset = false) =>
    (event) => {
      if (apply && !open) {
        onApplyFilter(filterParams);
      }
      if (event.type === 'keydown' && (event.key === 'Tab' || event.key === 'Shift')) {
        return;
      }
      if (reset) {
        onReset();
        setFilterData(initialFilterData);
        setFilterParams({});
        setFilterApplied(false);
      }

      setFilter(open);
    };

  const selectChipsHandler = (columnName, v) => {
    setFilterParams((prev) => {
      let newObject = { ...prev, [columnName]: v };
      if (columnName === FILTER_VALUES.COUNTRY) {
        newObject[FILTER_VALUES.STATE] = null;
        newObject[FILTER_VALUES.CITY] = [];
        getStatesBasedOnCountryId(v);
      } else if (columnName === FILTER_VALUES.STATE) {
        newObject[FILTER_VALUES.CITY] = [];
        getCitiesBasedOnStateId(v);
      } else if (columnName === FILTER_VALUES.DATE_RANGE) {
        if (FILTER_VALUES.SEEDLIVE_MISSING_DATES in prev) {
          const isMissingDatesDisabled = !hasCompleteDateRange(v);
          setFilterData((prevFilterData) => syncSeedliveMissingDatesToggle(prevFilterData, isMissingDatesDisabled));
          if (isMissingDatesDisabled) {
            newObject[FILTER_VALUES.SEEDLIVE_MISSING_DATES] = false;
          }
        }
      }

      if (APPROVED_POSTING_DATE_FIELDS.has(columnName)) {
        newObject = validateApprovedPostingDateRange(prev, newObject, columnName);
        setFilterData((prevFilterData) => syncApprovedPostingDateBounds(prevFilterData, newObject));
      }

      return newObject;
    });
  };

  const handleSort = (e) => {
    onSortSelect(e);
    toggleDrawer(false)(e);
  };

  const renderSortComponent = () => <SortContent selectedSort={selectedSort} data={sortData} handleSort={handleSort} />;
  const renderFilterComponent = () => (
    <FilterContent data={filterData} filterParams={filterParams} onChipChange={selectChipsHandler} />
  );

  const renderSubComponent = () => {
    switch (drawerType) {
      case DRAWER_TYPES.SORT_DRAWER:
        return renderSortComponent();
      case DRAWER_TYPES.FILTER_DRAWER:
        return renderFilterComponent();

      default:
        return;
    }
  };

  useEffect(() => {
    if (!filter) {
      setFilterParams(filteredValues);
    }
  }, [filter]);

  const subComponent = () => (
    <Box sx={{ width: width }} role="presentation">
      <GridContainer direction="column" className={classes.filterContainer} spacing={2}>
        <GridContainer p={2} pr={3}>
          <GridItem xs={8} md={10} display="flex" justifyContent="start" alignItems="center">
            <Typography role="presentation" variant="h1" data-testid="title">
              {title}
            </Typography>
          </GridItem>
          <GridItem xs={4} md={2} display="flex" justifyContent="end" alignItems="center">
            <IconButton onClick={toggleDrawer(false)} data-testid="closeBtn">
              <CloseIcon />
            </IconButton>
          </GridItem>
        </GridContainer>
        <GridContainer direction="column" className={classes.filterContainerList}>
          {renderSubComponent()}
        </GridContainer>
        {drawerType !== DRAWER_TYPES.SORT_DRAWER && (
          <GridContainer px={2} className={classes.filterBtnWrap}>
            <GridItem xs={6}>
              <Button variant="contained" onClick={toggleDrawer(false, true)} data-testid="apply-filter" size="small">
                APPLY FILTERS
              </Button>
            </GridItem>
            <GridItem xs={6}>
              <Button onClick={toggleDrawer(false, false, true)} data-testid="clear-all" size="small">
                CLEAR ALL
              </Button>
            </GridItem>
          </GridContainer>
        )}
      </GridContainer>
    </Box>
  );

  return (
    <Box>
      <Box>
        <Box component="div" sx={{ display: 'none' }} data-testid="drawer-status">
          {String(filter)}
        </Box>
        <Button
          className={classes.iconBtn}
          onClick={toggleDrawer(true)}
          startIcon={startIconParam}
          variant={isFilterApplied ? 'contained' : 'outlined'}
          sx={isFilterApplied ? undefined : { backgroundColor: 'common.white' }}
          data-testid="filter-btn"
          size="large"
        >
          <Typography variant="buttonText" noWrap className={classes.filterName}>
            {name}
          </Typography>
        </Button>
        <Drawer anchor={DIRECTION.RIGHT} open={filter} onClose={toggleDrawer(false)}>
          {subComponent()}
        </Drawer>
      </Box>
    </Box>
  );
}
